import {useEffect, useState} from "react";
import {useForm} from "antd/es/form/Form";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {StaffItem} from "../types/staff";
import {useParams} from "react-router-dom";
import {staffService} from "../services/staffService";
import {Query} from "appwrite";
import {createFeedbackHandlerProps, FeedbackItem} from "../types/feedback";
import {feedbackService} from "../services/feedbackService";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";
import {useAuth} from "../context/authContext";


export const useStaffController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState<StaffItem | undefined>(undefined)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreateFeedbackModalOpen, setIsCreateFeedbackModalOpen] = useState(false)

  const [staffList, setStaffList] = useState<StaffItem[]>([])
  const [feedbacksList, setFeedbacksList] = useState<FeedbackItem[]>([])
  const [lastUpdate, setLastUpdate] = useState(0)
  const [staffForm] = useForm()
  const [feedbackForm] = useForm()
  const {departmentId, officeId} = useParams()
  const {isLoggedIn} = useAuth()


  useEffect(() => {
    if (selectedStaff && isLoggedIn) {
      fetchStaffFeedbacks([Query.equal("staff", selectedStaff?.$id || "")])
      // check
    }
  }, [selectedStaff, isLoggedIn])


  useEffect(() => {
    if (!selectedStaff) {
      staffForm.resetFields()
    }
  }, [selectedStaff])

  useEffect(() => {
    if (!isCreateModalOpen) {
      staffForm.resetFields()
      setSelectedStaff(undefined)
    }
  }, [isCreateModalOpen])


  const confirmDeleteStaff = (props: { staffId: string, staffName: string }) => {
    const {staffName, staffId} = props
    const {confirm} = Modal;

    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити ${staffName} ?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteStaff(staffId)
        },
      });

  }

  const confirmDeleteFeedback = (props: { id: string }) => {
    const {confirm} = Modal;
    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити відгук?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteFeedbackHandler(props.id)
        },
      });
  }


  const toggleDetailModal = (props: { selectedStaff: StaffItem | undefined, isModalOpen: boolean }) => {
    const {selectedStaff, isModalOpen} = props
    setSelectedStaff(selectedStaff)
    setIsDetailModalOpen(isModalOpen)
  }
  const toggleCreateModal = (props: { selectedStaff: StaffItem | undefined, isModalOpen: boolean }) => {
    const {selectedStaff, isModalOpen} = props;
    setSelectedStaff(selectedStaff)
    setIsCreateModalOpen(isModalOpen)
  }

  const editStaffButtonHandler = (staffItem: StaffItem) => {
    staffForm.setFieldsValue(staffItem)
    toggleCreateModal({selectedStaff: staffItem, isModalOpen: true})
  }


  const list = staffList // ???

  const fetchStaffList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await staffService.list(queries)
      setStaffList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження лікарів!"});
    } finally {
      setIsLoading(false)
    }
  }

  const createStaff = async (data: StaffItem) => {
    setIsLoading(true)
    if (officeId) {
      data.offices = officeId
    }
    try {
      await staffService.createStaff(data)
      openSuccessNotification("Лікаря успішно додано!")
      toggleCreateModal({isModalOpen: false, selectedStaff: undefined})
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка створення лікаря!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const updateStaff = async (data: StaffItem) => {
    setIsLoading(true)
    try {
      await staffService.updateStaff({$id: selectedStaff?.$id, ...data})
      toggleCreateModal({isModalOpen: false, selectedStaff: undefined})
      toggleLastUpdate()
      openSuccessNotification("Дані лікаря успішно оновлено!")
    } catch (error) {
      showErrorNotification({message: "Помилка оновлення даних лікаря!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const addFeedback = async (data: FeedbackItem) => {
    setIsLoading(true)
    try {
      await feedbackService.create(data)
      toggleCreateFeedbackModal()
      openSuccessNotification("Дякуємо за відгук!")
    } catch (error) {
      showErrorNotification({message: "Сталася помилка. Спробуйте пізніше!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const deleteFeedbackHandler = async (id: string) => {
    setIsLoading(true)
    try {
      await feedbackService.delete(id)
      const updatedList = feedbacksList.filter(feedback => feedback.$id !== id)
      setFeedbacksList(updatedList)
      openSuccessNotification("Відгук успішно видалено!")
    } catch (error) {
      showErrorNotification({message: "Помилка видалення відгуку!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const deleteStaff = async (id: string) => {
    setIsLoading(true)
    try {
      await staffService.delete(id)
      openSuccessNotification("Лікаря було успішно видалено!")
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка видалення лікаря!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const createStaffHandler = async (data: StaffItem) => {
    const correctData = {
      ...data,
      department: departmentId
    }
    await createStaff(correctData)
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now())
  }

  const toggleCreateFeedbackModal = () => {
    const value = !isCreateFeedbackModalOpen
    setIsCreateFeedbackModalOpen(value)
    if (!value) {
      feedbackForm.resetFields()
      setFeedbacksList([])
    }
  }

  const createFeedbackHandler = async (formData: createFeedbackHandlerProps) => {
    await addFeedback(formData)
  }

  const fetchStaffFeedbacks = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await feedbackService.list(queries)
      setFeedbacksList(list)
    } catch (error) {
      showErrorNotification({message: "Помилка завантаження відгуків!", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }


  return {
    editStaffButtonHandler,
    confirmDeleteStaff,
    setSelectedStaff,
    list,
    isLoading,
    selectedStaff,
    isDetailModalOpen,
    isCreateModalOpen,
    toggleDetailModal,
    toggleCreateModal,
    staffForm,
    createStaffHandler,
    staffList,
    feedbackForm,
    toggleCreateFeedbackModal,
    isCreateFeedbackModalOpen,
    createFeedbackHandler,
    feedbacksList,
    fetchStaffList,
    updateStaff,
    confirmDeleteFeedback,
    lastUpdate
  }
}
